import type { OptionPerformanceSummary } from 'venn-api';
import { assertNotNil, type CustomizableMetric, Dates, useHasFF } from 'venn-utils';
import type { ColDef, ColGroupDef } from 'ag-grid-community';
import type { StudioRequestSubject } from 'venn-state';
import { formatExportableSubjectWithOptionalFee } from '../../../legend/NameWithOptionalFee';
import { useGetRequestSubjectFee } from '../../utils/hooks/use-advisory-fee';
import HeaderCellRenderer from '../../../studio-blocks/components/grid/renderers/HeaderCellRenderer';
import { BOLD_CLASS, LEFT_ALIGN_CLASS, RIGHT_ALIGN_CLASS } from '../../../studio-blocks/customAnalysisContants';
import ValueCellRenderer from '../../../studio-blocks/components/grid/renderers/ValueCellRenderer';
import { getCellClassForDataType, getDefaultValueFormat } from '../../../studio-blocks/logic/gridStyling';
import type { OptionsPerformanceSummaryBlockMetricKeys } from '../../../../../venn-utils/src/blocksettings/optionsPerformanceSummaryBlockMetrics';
import { Match } from 'effect';
import { BasicHeaderRenderer } from '../../../studio-blocks/components/grid/renderers/BasicHeaderRenderer';
import { useTheme } from 'styled-components';
import { parseOptionSymbol } from '../../../../../venn-utils/src/options/utils';

const getColumnWidth = (key: OptionsPerformanceSummaryBlockMetricKeys): number | undefined => {
  switch (key) {
    case 'symbol':
    case 'optionType':
      return 70;
    case 'strike':
      return 70;
    case 'expiration':
      return 100;
    case 'exerciseStyle':
      return 70;
    case 'impliedVolatility':
    case 'underlyingSpotPrice':
    case 'currentOptionPrice':
    case 'breakEvenPrice':
    case 'bestBid':
    case 'bestOffer':
    case 'openInterest':
    case 'volume':
      return 80;
    case 'moneyness':
      return 90;
    case 'delta':
    case 'gamma':
    case 'theta':
    case 'vega':
      return 60;
    default:
      return undefined;
  }
};

const getColumnDefSharedProps = ({
  key,
  textAlign,
  color,
}: {
  key?: OptionsPerformanceSummaryBlockMetricKeys;
  textAlign: 'left' | 'right';
  color: string;
}): Partial<ColDef<OptionPerformanceSummary>> => {
  return {
    headerComponent: BasicHeaderRenderer,
    headerComponentParams: {
      style: { textAlign, width: '100%', color },
    },
    wrapText: true,
    autoHeight: true,
    cellStyle: {
      wordBreak: 'normal',
    },
    sortable: false,
    ...(key ? { maxWidth: getColumnWidth(key) } : {}),
  };
};

export const useOptionsPerformanceSummaryColumnDefs = ({
  subject,
  selectedMetrics,
}: {
  subject: StudioRequestSubject | undefined;
  selectedMetrics: CustomizableMetric[];
}): (ColDef<OptionPerformanceSummary> | ColGroupDef<OptionPerformanceSummary>)[] => {
  const skipKSuffixInFormatting = useHasFF('skip_k_suffix_in_formatting_ff');
  const getRequestSubjectFee = useGetRequestSubjectFee();
  const theme = useTheme();

  if (!subject) {
    return [];
  }

  return [
    {
      headerName: formatExportableSubjectWithOptionalFee(subject, false, getRequestSubjectFee({ subject })),
      headerGroupComponent: HeaderCellRenderer,
      headerGroupComponentParams: {
        subject,
      },
      marryChildren: true,
      children: [
        {
          colId: 'investmentName',
          headerName: 'Option Name',
          valueGetter: ({ data }) => data?.investmentName,
          headerClass: ['ag-left-aligned-header'],
          cellClass: [BOLD_CLASS, LEFT_ALIGN_CLASS],
          flex: 1,
          minWidth: 200,
          ...getColumnDefSharedProps({
            textAlign: 'left',
            color: theme.Colors.Black,
          }),
        },
        ...selectedMetrics.map(
          (metric) =>
            ({
              colId: metric.key,
              headerName: metric.label,
              cellRenderer: ValueCellRenderer,
              cellRendererParams: {
                type: metric.dataType,
                skipKSuffixInFormatting,
              },
              ...getColumnDefSharedProps({
                key: metric.key as OptionsPerformanceSummaryBlockMetricKeys,
                textAlign: 'right',
                color: theme.Colors.Black,
              }),
              valueGetter: ({ data }) => {
                const metricKey = metric.key as OptionsPerformanceSummaryBlockMetricKeys;

                return Match.value(metricKey).pipe(
                  Match.when('symbol', () => {
                    const parsedOptionData = parseOptionSymbol(data?.symbol);
                    return parsedOptionData?.underlyingSymbol ?? '';
                  }),
                  Match.when('optionType', () => {
                    const optionType = data?.optionType;
                    return Match.value(optionType).pipe(
                      Match.when('CALL', () => 'Call'),
                      Match.when('PUT', () => 'Put'),
                      Match.orElse(() => ''),
                    );
                  }),
                  Match.when('expiration', () => {
                    const expiration = data?.expiration;
                    return expiration ? Dates.toDDMMMYYYY(expiration, 'DAILY') : '';
                  }),
                  Match.when('exerciseStyle', () => {
                    const exerciseStyle = data?.exerciseStyle;
                    return Match.value(exerciseStyle).pipe(
                      Match.when('AMERICAN', () => 'A'),
                      Match.when('EUROPEAN', () => 'E'),
                      Match.orElse(() => ''),
                    );
                  }),
                  Match.orElse(() => {
                    const metricPath = assertNotNil(metric.metricPath) as keyof OptionPerformanceSummary;
                    return data?.[metricPath];
                  }),
                );
              },
              valueFormatter: getDefaultValueFormat,
              cellClass: [RIGHT_ALIGN_CLASS, getCellClassForDataType(metric.dataType)],
            }) satisfies ColDef<OptionPerformanceSummary>,
        ),
      ] satisfies ColDef<OptionPerformanceSummary>[],
    },
  ];
};
